import { useParams, Link } from 'react-router-dom';
import { useState, useEffect } from 'react';
import {
  Calendar,
  Clock,
  ArrowLeft,
  Share2,
  Twitter,
  Linkedin,
  Link as LinkIcon,
  Eye,
  Heart,
  MessageSquare,
  ChevronRight,
  BookOpen,
  Tag,
  ArrowRight,
  CheckCircle2,
  Copy
} from 'lucide-react';

interface BlogPost {
  slug: string;
  title: string;
  excerpt: string;
  content: string;
  image: string;
  author: {
    name: string;
    avatar: string;
    role: string;
    bio: string;
  };
  date: string;
  readTime: string;
  category: string;
  tags: string[];
  views: number;
}

// Turkish blog posts data
export const blogPostsData: Record<string, BlogPost> = {
  'yapay-zeka-yazilim-gelistirme-2025': {
    slug: 'yapay-zeka-yazilim-gelistirme-2025',
    title: 'Yapay Zeka ile Yazılım Geliştirme: 2025 Trendleri',
    excerpt: 'AI destekli kod editörleri, otomatik test üretimi ve akıllı refactoring araçları ile yazılım geliştirme süreçleri nasıl dönüşüyor?',
    content: `
## Yazılım Geliştirmenin Yeni Çağı

2025 yılına girerken, yapay zeka artık yazılım geliştirme süreçlerinin ayrılmaz bir parçası haline geldi. Bu yazıda, AI destekli geliştirme araçlarının nasıl evrildiğini ve gelecekte bizi nelerin beklediğini inceliyoruz.

### AI Destekli Kod Editörleri

Modern kod editörleri artık sadece syntax highlighting yapmakla kalmıyor. Yapay zeka entegrasyonu sayesinde:

- **Akıllı kod tamamlama**: Bağlamı anlayan ve projenizin stiline uygun öneriler sunan sistemler
- **Hata öncesi tespit**: Kodu yazmadan önce potansiyel hataları öngören algoritmalar
- **Otomatik refactoring**: Kod kalitesini artıran akıllı yeniden yapılandırma önerileri

\`\`\`typescript
// AI destekli kod tamamlama örneği
const userService = new UserService({
  repository: userRepository,
  cache: redisCache,
  logger: appLogger // AI bağlamı anlayarak öneriyor
});
\`\`\`

### Otomatik Test Üretimi

AI artık sadece kod yazmakla kalmıyor, test senaryolarını da otomatik olarak üretebiliyor:

1. **Unit test generation**: Fonksiyonların edge case'lerini tespit ederek kapsamlı testler oluşturma
2. **Integration test suggestions**: Bileşenler arası etkileşimleri analiz ederek entegrasyon testleri önerme
3. **E2E test scenarios**: Kullanıcı akışlarını simüle eden uçtan uca test senaryoları

### Performans Artışı

Araştırmalarımıza göre, AI destekli geliştirme araçları kullanıldığında:

- **3x** daha hızlı kod yazımı
- **%60** daha az bug
- **%40** daha kısa code review süresi

### Geleceğe Bakış

2025'te AI destekli geliştirme araçlarından beklentilerimiz:

- Doğal dil ile programlama arayüzleri
- Otomatik güvenlik açığı tespiti ve düzeltme
- Çoklu proje bağlamı anlama
- Gerçek zamanlı mimari önerileri

---

Yapay zeka, yazılım geliştirmeyi demokratikleştiriyor ve her seviyeden geliştiricinin daha kaliteli kod üretmesini sağlıyor.
    `,
    image: 'https://images.unsplash.com/photo-1677442136019-21780ecad995?w=1200&h=600&fit=crop',
    author: {
      name: 'Dr. Ahmet Yılmaz',
      avatar: 'AY',
      role: 'AI Araştırma Direktörü',
      bio: 'Yapay zeka ve makine öğrenmesi alanında 10 yılı aşkın deneyime sahip. Türkçe NLP ve kod üretimi üzerine akademik çalışmalar yürütüyor.'
    },
    date: '25 Aralık 2024',
    readTime: '8 dk',
    category: 'Yapay Zeka',
    tags: ['Yapay Zeka', 'Yazılım', 'Trendler', 'AI', 'Kod Editörü'],
    views: 3420
  },
  'turkce-llm-modelleri-gelecegi': {
    slug: 'turkce-llm-modelleri-gelecegi',
    title: 'Türkçe LLM Modelleri: Yerli AI\'ın Geleceği',
    excerpt: 'Türkçe\'ye özel optimize edilmiş büyük dil modelleri neden önemli? Ochtopus AI projemizden edindiğimiz deneyimler.',
    content: `
## Türkçe AI: Neden Yerli Model?

Büyük dil modelleri (LLM) dünyası hızla gelişirken, Türkçe için optimize edilmiş modellerin önemi giderek artıyor. Bu yazıda, Türkçe LLM geliştirme sürecinde karşılaştığımız zorlukları ve çözümleri paylaşıyoruz.

### Türkçe'nin Zorlukları

Türkçe, agglutinative (eklemeli) bir dil olarak, AI modelleri için benzersiz zorluklar sunuyor:

- **Ek yapısı**: Bir kelime onlarca ek alabilir
- **Söz dizimi esnekliği**: Özne-yüklem sırası değişken
- **Vowel harmony**: Ünlü uyumu kuralları
- **Suffix complexity**: Karmaşık son ek yapıları

### Ochtopus AI Deneyimi

Kendi Türkçe AI modelimizi geliştirirken edindiğimiz önemli dersler:

1. **Veri kalitesi kritik**: Temiz ve çeşitli Türkçe veri seti oluşturmak en önemli adım
2. **Tokenization stratejisi**: Türkçe için özel tokenizer geliştirmek performansı %30 artırdı
3. **Fine-tuning yaklaşımı**: Transfer learning ile küçük veri setlerinde bile iyi sonuçlar

\`\`\`python
# Türkçe tokenization örneği
tokenizer = TurkishTokenizer(
    vocab_size=50000,
    model_type="unigram",
    character_coverage=0.9995
)
\`\`\`

### Başarı Metrikleri

Geliştirdiğimiz Türkçe model ile elde ettiğimiz sonuçlar:

- BLEU skoru: **0.42** (çeviri görevleri)
- Perplexity: **18.5** (dil modelleme)
- F1 skoru: **0.89** (NER görevleri)

### Gelecek Planları

Türkçe AI alanında hedeflerimiz:

- Kod üretimi için Türkçe prompt desteği
- Hukuki ve tıbbi alanlarda uzmanlaşmış modeller
- Sesli asistan entegrasyonları

---

Türkçe AI modelleri, sadece teknik bir başarı değil, aynı zamanda dijital bağımsızlık için stratejik bir yatırımdır.
    `,
    image: 'https://images.unsplash.com/photo-1620712943543-bcc4688e7485?w=1200&h=600&fit=crop',
    author: {
      name: 'Zeynep Kaya',
      avatar: 'ZK',
      role: 'NLP Mühendisi',
      bio: 'Doğal dil işleme ve Türkçe AI modelleri konusunda uzman. Boğaziçi Üniversitesi Bilgisayar Mühendisliği mezunu.'
    },
    date: '22 Aralık 2024',
    readTime: '10 dk',
    category: 'Yapay Zeka',
    tags: ['NLP', 'Türkçe', 'LLM', 'AI', 'Machine Learning'],
    views: 2890
  },
  'kurumsal-dijital-donusum-rehberi': {
    slug: 'kurumsal-dijital-donusum-rehberi',
    title: 'Kurumsal Dijital Dönüşüm: A\'dan Z\'ye Rehber',
    excerpt: 'Excel\'den bulut tabanlı sistemlere, legacy yazılımlardan modern mimarilere geçiş süreci.',
    content: `
## Dijital Dönüşüm Neden Kaçınılmaz?

8 yıllık deneyimimizde, onlarca şirketin dijital dönüşüm yolculuğuna eşlik ettik. Bu rehberde, başarılı bir dönüşüm için gerekli adımları paylaşıyoruz.

### Mevcut Durumu Analiz Edin

Dönüşüme başlamadan önce, mevcut sistemlerinizi detaylı analiz edin:

- **Süreç haritası**: Tüm iş süreçlerini belgelendirin
- **Teknoloji envanteri**: Kullanılan araçları ve entegrasyonları listeleyin
- **Pain points**: Darboğazları ve verimsizlikleri tespit edin

### Aşamalı Geçiş Stratejisi

Büyük patlama yaklaşımı yerine, aşamalı geçişi öneriyoruz:

1. **Pilot proje**: Düşük riskli bir departmanla başlayın
2. **Ölçeklenebilir mimari**: Gelecekteki büyümeyi göz önünde bulundurun
3. **Paralel çalışma**: Eski ve yeni sistemleri geçiş süresince birlikte işletin

### Cloud Migration

Legacy sistemlerden buluta geçiş stratejileri:

\`\`\`
Lift & Shift → Replatform → Refactor → Rebuild
     ↓              ↓           ↓          ↓
  Hızlı        Optimize      Modern     Cloud-Native
\`\`\`

### Başarı Kriterleri

Dijital dönüşümün başarısını ölçmek için:

- Operasyonel verimlilik artışı
- Müşteri memnuniyeti skorları
- Çalışan benimseme oranları
- ROI hesaplamaları

---

Dijital dönüşüm bir hedef değil, sürekli bir yolculuktur. Doğru strateji ve güvenilir bir partner ile bu yolculuk çok daha kolay olabilir.
    `,
    image: 'https://images.unsplash.com/photo-1522071820081-009f0129c71c?w=1200&h=600&fit=crop',
    author: {
      name: 'Mehmet Demir',
      avatar: 'MD',
      role: 'Çözüm Mimarı',
      bio: 'Kurumsal yazılım çözümleri ve dijital dönüşüm danışmanlığı alanında 15 yıllık deneyim. SAP ve Microsoft sertifikalı.'
    },
    date: '20 Aralık 2024',
    readTime: '12 dk',
    category: 'Kurumsal',
    tags: ['Dijital Dönüşüm', 'Kurumsal', 'Cloud', 'Enterprise'],
    views: 1850
  },
  'siber-guvenlik-tehditleri-2025': {
    slug: 'siber-guvenlik-tehditleri-2025',
    title: '2025\'te Dikkat Edilmesi Gereken Siber Güvenlik Tehditleri',
    excerpt: 'Ransomware, supply chain saldırıları ve AI destekli siber tehditler.',
    content: `
## Siber Tehditler Evrim Geçiriyor

2025 yılında siber güvenlik tehditleri daha sofistike ve hedefli hale geliyor. Bu yazıda, dikkat etmeniz gereken tehditleri ve alınması gereken önlemleri inceliyoruz.

### Ransomware 3.0

Yeni nesil ransomware saldırıları:

- **Triple extortion**: Şifreleme + veri sızıntısı + DDoS tehdidi
- **RaaS (Ransomware as a Service)**: Organize suç gruplarının hizmeti
- **Hedefli saldırılar**: Kritik altyapılar ve sağlık sektörü odaklı

### Supply Chain Saldırıları

Tedarik zinciri saldırıları artıyor:

1. Yazılım güncellemeleri üzerinden sızma
2. Üçüncü taraf bağımlılıkların exploit edilmesi
3. CI/CD pipeline'larına saldırı

### AI Destekli Tehditler

Yapay zeka artık saldırganların da elinde:

- **Deepfake phishing**: Gerçekçi ses ve video ile sosyal mühendislik
- **Automated vulnerability discovery**: AI ile zafiyet tarama
- **Adaptive malware**: Tespit sistemlerinden kaçan akıllı zararlılar

### Koruma Stratejileri

Şirketinizi korumak için:

\`\`\`
Zero Trust + EDR + SIEM + Security Awareness
         ↓
    Defense in Depth
\`\`\`

---

Siber güvenlik artık sadece IT departmanının sorumluluğu değil, tüm organizasyonun önceliği olmalıdır.
    `,
    image: 'https://images.unsplash.com/photo-1563986768609-322da13575f3?w=1200&h=600&fit=crop',
    author: {
      name: 'Can Arslan',
      avatar: 'CA',
      role: 'Güvenlik Uzmanı',
      bio: 'OSCP, CEH sertifikalı güvenlik uzmanı. Penetration testing ve güvenlik denetimi konularında 8 yıllık deneyim.'
    },
    date: '18 Aralık 2024',
    readTime: '7 dk',
    category: 'Siber Güvenlik',
    tags: ['Siber Güvenlik', 'Ransomware', 'Tehditler', 'Security'],
    views: 2340
  },
  'react-vs-vue-vs-angular-2025': {
    slug: 'react-vs-vue-vs-angular-2025',
    title: 'React vs Vue vs Angular: 2025 Karşılaştırması',
    excerpt: 'Frontend framework seçimi hala zor mu? Performans, öğrenme eğrisi ve ekosistem açısından güncel karşılaştırma.',
    content: `
## Framework Savaşları 2025

Frontend dünyasının üç büyük framework'ü arasındaki rekabet devam ediyor. 2025 perspektifinden bakarak hangisini seçmeniz gerektiğini değerlendiriyoruz.

### Performans Karşılaştırması

Modern benchmark sonuçları:

| Metrik | React | Vue | Angular |
|--------|-------|-----|---------|
| Bundle Size | 42KB | 33KB | 130KB |
| Startup Time | 320ms | 280ms | 450ms |
| Memory Usage | 8.5MB | 7.2MB | 12MB |

### Öğrenme Eğrisi

Her framework için öğrenme süreci:

- **React**: Minimal API, ama ekosistem karmaşık
- **Vue**: En düşük giriş bariyeri, progressive adoption
- **Angular**: Kapsamlı ama dik öğrenme eğrisi

### Ekosistem ve Topluluk

2025'te ekosistem durumu:

\`\`\`
React: Next.js, Remix, React Query, Zustand
Vue: Nuxt.js, Pinia, Vue Query
Angular: Angular Universal, NgRx, Angular Material
\`\`\`

### Hangisini Seçmeli?

Projenize göre önerilerimiz:

1. **Startup/MVP**: Vue veya React
2. **Enterprise**: Angular veya React
3. **Performans kritik**: React veya Vue
4. **Tam çözüm**: Angular

---

Doğru framework, projenin gereksinimlerine ve ekibin deneyimine bağlıdır. Her üçü de production-ready ve güvenilir seçeneklerdir.
    `,
    image: 'https://images.unsplash.com/photo-1555949963-aa79dcee981c?w=1200&h=600&fit=crop',
    author: {
      name: 'Elif Özkan',
      avatar: 'EO',
      role: 'Frontend Lead',
      bio: 'React ve Vue ekosistemlerinde uzman. 50+ projenin frontend mimarisinden sorumlu.'
    },
    date: '15 Aralık 2024',
    readTime: '9 dk',
    category: 'Yazılım',
    tags: ['React', 'Vue', 'Angular', 'Frontend', 'JavaScript'],
    views: 4120
  }
};

// Get all posts as array for listing
export const getAllBlogPosts = () => Object.values(blogPostsData);

// Reading Progress Bar Component
function ReadingProgressBar() {
  const [progress, setProgress] = useState(0);

  useEffect(() => {
    const handleScroll = () => {
      const windowHeight = window.innerHeight;
      const documentHeight = document.documentElement.scrollHeight - windowHeight;
      const scrollTop = window.scrollY;
      const progress = (scrollTop / documentHeight) * 100;
      setProgress(Math.min(progress, 100));
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  return (
    <div className="fixed top-0 left-0 right-0 h-1 bg-bg-elevated z-50">
      <div
        className="h-full bg-gradient-to-r from-purple-500 to-fuchsia-500 transition-all duration-150"
        style={{ width: `${progress}%` }}
      />
    </div>
  );
}

// Table of Contents Component with Active Section Tracking
function TableOfContents({ content }: { content: string }) {
  const [activeId, setActiveId] = useState<string>('');

  const headings = content
    .split('\n')
    .filter(line => line.startsWith('## ') || line.startsWith('### '))
    .map(line => ({
      level: line.startsWith('### ') ? 3 : 2,
      text: line.replace(/^##+ /, ''),
      id: line.replace(/^##+ /, '').toLowerCase().replace(/\s+/g, '-').replace(/[^\w-]/g, '')
    }));

  useEffect(() => {
    const handleScroll = () => {
      const scrollPosition = window.scrollY + 150; // Offset for header

      // Find the heading that is currently in view
      let currentActiveId = '';

      for (const heading of headings) {
        const element = document.getElementById(heading.id);
        if (element) {
          const elementTop = element.offsetTop;
          if (scrollPosition >= elementTop) {
            currentActiveId = heading.id;
          }
        }
      }

      setActiveId(currentActiveId);
    };

    // Initial check
    handleScroll();

    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, [headings]);

  const handleClick = (e: React.MouseEvent<HTMLAnchorElement>, id: string) => {
    e.preventDefault();
    const element = document.getElementById(id);
    if (element) {
      const offset = 100; // Header height offset
      const elementPosition = element.getBoundingClientRect().top;
      const offsetPosition = elementPosition + window.pageYOffset - offset;

      window.scrollTo({
        top: offsetPosition,
        behavior: 'smooth'
      });

      // Update URL hash without jumping
      window.history.pushState(null, '', `#${id}`);
      setActiveId(id);
    }
  };

  if (headings.length === 0) return null;

  return (
    <div className="card">
      <h3 className="font-semibold mb-4 flex items-center gap-2">
        <BookOpen size={18} className="text-purple-400" />
        İçindekiler
      </h3>
      <nav className="space-y-1">
        {headings.map((heading, index) => {
          const isActive = activeId === heading.id;
          return (
            <a
              key={index}
              href={`#${heading.id}`}
              onClick={(e) => handleClick(e, heading.id)}
              className={`block text-sm py-1.5 px-3 rounded-lg transition-all duration-200 ${
                heading.level === 3 ? 'ml-3' : ''
              } ${
                isActive
                  ? 'bg-purple-500/10 text-purple-400 font-medium border-l-2 border-purple-500'
                  : 'text-text-secondary hover:text-purple-400 hover:bg-bg-elevated border-l-2 border-transparent'
              }`}
            >
              {heading.text}
            </a>
          );
        })}
      </nav>
    </div>
  );
}

export function BlogDetailPage() {
  const { slug } = useParams<{ slug: string }>();
  const post = slug ? blogPostsData[slug] : null;
  const [copied, setCopied] = useState(false);
  const [liked, setLiked] = useState(false);

  const handleCopyLink = () => {
    navigator.clipboard.writeText(window.location.href);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  if (!post) {
    return (
      <div className="pt-24 min-h-screen">
        <div className="container py-16 text-center">
          <div className="w-20 h-20 rounded-full bg-purple-500/10 flex items-center justify-center mx-auto mb-6">
            <BookOpen className="text-purple-400" size={40} />
          </div>
          <h1 className="heading-lg mb-4">Yazı Bulunamadı</h1>
          <p className="text-text-secondary mb-8">
            Aradığınız blog yazısı mevcut değil veya kaldırılmış olabilir.
          </p>
          <Link to="/blog" className="btn btn-primary">
            <ArrowLeft size={18} />
            Blog'a Dön
          </Link>
        </div>
      </div>
    );
  }

  // Calculate related posts based on category and tags similarity
  const relatedPosts = getAllBlogPosts()
    .filter(p => p.slug !== post.slug)
    .map(p => {
      let score = 0;
      // Same category = 3 points
      if (p.category === post.category) score += 3;
      // Matching tags = 1 point each
      const matchingTags = p.tags.filter(tag => post.tags.includes(tag));
      score += matchingTags.length;
      return { ...p, relevanceScore: score };
    })
    .filter(p => p.relevanceScore > 0) // Only show if there's some relevance
    .sort((a, b) => b.relevanceScore - a.relevanceScore) // Sort by relevance
    .slice(0, 10); // Max 10 related posts

  const shareUrl = `https://bosphoruslabs.com/blog/${post.slug}`;

  // Render markdown content
  const renderContent = (content: string) => {
    return content.split('\n').map((line, i) => {
      // Headings
      if (line.startsWith('## ')) {
        const text = line.replace('## ', '');
        const id = text.toLowerCase().replace(/\s+/g, '-').replace(/[^\w-]/g, '');
        return <h2 key={i} id={id} className="heading-lg mt-12 mb-6 scroll-mt-24">{text}</h2>;
      }
      if (line.startsWith('### ')) {
        const text = line.replace('### ', '');
        const id = text.toLowerCase().replace(/\s+/g, '-').replace(/[^\w-]/g, '');
        return <h3 key={i} id={id} className="heading-md mt-8 mb-4 scroll-mt-24">{text}</h3>;
      }

      // Bold text
      if (line.startsWith('**') && line.endsWith('**')) {
        return <p key={i} className="font-semibold text-text-primary mt-6 mb-2">{line.replace(/\*\*/g, '')}</p>;
      }

      // Code blocks
      if (line.startsWith('```')) {
        return null;
      }

      // Inline code detection
      if (line.includes('`') && !line.startsWith('```')) {
        const parts = line.split(/(`[^`]+`)/g);
        return (
          <p key={i} className="text-text-secondary mb-4 leading-relaxed">
            {parts.map((part, j) => {
              if (part.startsWith('`') && part.endsWith('`')) {
                return (
                  <code key={j} className="px-1.5 py-0.5 rounded bg-purple-500/10 text-purple-400 text-sm font-mono">
                    {part.slice(1, -1)}
                  </code>
                );
              }
              return part;
            })}
          </p>
        );
      }

      // Bullet lists
      if (line.startsWith('- ')) {
        return (
          <li key={i} className="flex items-start gap-3 text-text-secondary mb-2">
            <span className="w-1.5 h-1.5 rounded-full bg-purple-400 mt-2.5 flex-shrink-0" />
            <span>{line.replace('- ', '')}</span>
          </li>
        );
      }

      // Numbered lists
      if (/^\d+\.\s/.test(line)) {
        const num = line.match(/^(\d+)\./)?.[1];
        return (
          <li key={i} className="flex items-start gap-3 text-text-secondary mb-2">
            <span className="w-6 h-6 rounded-full bg-purple-500/10 text-purple-400 text-xs flex items-center justify-center flex-shrink-0 mt-0.5">
              {num}
            </span>
            <span>{line.replace(/^\d+\.\s/, '')}</span>
          </li>
        );
      }

      // Blockquotes
      if (line.startsWith('> ')) {
        return (
          <blockquote key={i} className="border-l-4 border-purple-500 pl-4 my-6 italic text-text-secondary bg-purple-500/5 py-3 rounded-r-lg">
            {line.replace('> ', '')}
          </blockquote>
        );
      }

      // Tables - skip for simplicity
      if (line.startsWith('|')) {
        return null;
      }

      // Horizontal rule
      if (line.startsWith('---')) {
        return <hr key={i} className="my-8 border-border-default" />;
      }

      // Empty lines
      if (line.trim() === '') {
        return <br key={i} />;
      }

      // Regular paragraphs
      return <p key={i} className="text-text-secondary mb-4 leading-relaxed">{line}</p>;
    });
  };

  return (
    <div className="min-h-screen">
      <ReadingProgressBar />

      {/* Hero */}
      <section className="pt-32 pb-12 relative overflow-hidden">
        {/* Background */}
        <div className="absolute inset-0 bg-gradient-to-b from-purple-500/5 via-transparent to-transparent" />

        <div className="container relative">
          <div className="max-w-4xl mx-auto">
            {/* Breadcrumb */}
            <nav className="flex items-center gap-2 text-sm text-text-tertiary mb-6">
              <Link to="/" className="hover:text-purple-400 transition-colors">Ana Sayfa</Link>
              <ChevronRight size={14} />
              <Link to="/blog" className="hover:text-purple-400 transition-colors">Blog</Link>
              <ChevronRight size={14} />
              <span className="text-text-secondary">{post.category}</span>
            </nav>

            {/* Category & Meta */}
            <div className="flex flex-wrap items-center gap-3 mb-6">
              <span className="px-3 py-1.5 rounded-full bg-purple-500/10 text-purple-400 text-sm font-medium">
                {post.category}
              </span>
              <span className="flex items-center gap-1.5 text-text-tertiary text-sm">
                <Eye size={14} />
                {post.views.toLocaleString()} görüntülenme
              </span>
            </div>

            {/* Title */}
            <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold mb-6 leading-tight">
              {post.title}
            </h1>

            {/* Excerpt */}
            <p className="text-xl text-text-secondary mb-8">
              {post.excerpt}
            </p>

            {/* Author & Meta */}
            <div className="flex flex-wrap items-center justify-between gap-6 pb-8 border-b border-border-default">
              <div className="flex items-center gap-4">
                <div className="w-14 h-14 rounded-full bg-gradient-to-br from-purple-500 to-fuchsia-500 flex items-center justify-center text-white font-bold text-lg">
                  {post.author.avatar}
                </div>
                <div>
                  <div className="font-semibold">{post.author.name}</div>
                  <div className="text-text-tertiary text-sm">{post.author.role}</div>
                </div>
              </div>
              <div className="flex items-center gap-4 text-text-secondary text-sm">
                <span className="flex items-center gap-1.5">
                  <Calendar size={16} />
                  {post.date}
                </span>
                <span className="flex items-center gap-1.5">
                  <Clock size={16} />
                  {post.readTime} okuma
                </span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Featured Image */}
      <section className="pb-12">
        <div className="container">
          <div className="max-w-4xl mx-auto">
            <div className="rounded-2xl overflow-hidden shadow-2xl shadow-purple-500/10">
              <img
                src={post.image}
                alt={post.title}
                className="w-full h-auto object-cover"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Content */}
      <section className="pb-16">
        <div className="container">
          <div className="grid lg:grid-cols-[1fr_300px] gap-12 max-w-6xl mx-auto">
            {/* Article Content */}
            <article className="max-w-3xl">
              <div className="prose prose-lg prose-invert max-w-none">
                <div className="blog-content">
                  {renderContent(post.content)}
                </div>
              </div>

              {/* Tags */}
              <div className="mt-12 pt-8 border-t border-border-default">
                <div className="flex items-center gap-2 flex-wrap">
                  <Tag size={16} className="text-purple-400" />
                  {post.tags.map((tag) => (
                    <Link
                      key={tag}
                      to={`/blog?tag=${tag}`}
                      className="px-3 py-1.5 rounded-full bg-bg-card border border-border-default text-sm text-text-secondary hover:border-purple-500/50 hover:text-purple-400 transition-colors"
                    >
                      #{tag}
                    </Link>
                  ))}
                </div>
              </div>

              {/* Actions */}
              <div className="mt-8 flex items-center justify-between">
                <button
                  onClick={() => setLiked(!liked)}
                  className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-colors ${
                    liked ? 'bg-pink-500/10 text-pink-400' : 'bg-bg-card border border-border-default text-text-secondary hover:text-pink-400'
                  }`}
                >
                  <Heart size={18} className={liked ? 'fill-current' : ''} />
                  <span>{liked ? 'Beğenildi' : 'Beğen'}</span>
                </button>
                <div className="flex items-center gap-2">
                  <span className="text-text-tertiary text-sm mr-2">Paylaş:</span>
                  <a
                    href={`https://twitter.com/intent/tweet?url=${encodeURIComponent(shareUrl)}&text=${encodeURIComponent(post.title)}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="p-2.5 rounded-lg bg-bg-card border border-border-default hover:border-purple-500/50 hover:text-purple-400 transition-colors"
                  >
                    <Twitter size={18} />
                  </a>
                  <a
                    href={`https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(shareUrl)}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="p-2.5 rounded-lg bg-bg-card border border-border-default hover:border-purple-500/50 hover:text-purple-400 transition-colors"
                  >
                    <Linkedin size={18} />
                  </a>
                  <button
                    onClick={handleCopyLink}
                    className="p-2.5 rounded-lg bg-bg-card border border-border-default hover:border-purple-500/50 hover:text-purple-400 transition-colors relative"
                  >
                    {copied ? <CheckCircle2 size={18} className="text-green-400" /> : <Copy size={18} />}
                  </button>
                </div>
              </div>

              {/* Author Bio */}
              <div className="mt-12 p-6 rounded-2xl bg-gradient-to-br from-bg-card to-bg-surface border border-border-default">
                <div className="flex items-start gap-4">
                  <div className="w-16 h-16 rounded-full bg-gradient-to-br from-purple-500 to-fuchsia-500 flex items-center justify-center text-white text-xl font-bold flex-shrink-0">
                    {post.author.avatar}
                  </div>
                  <div>
                    <div className="font-semibold text-lg mb-1">{post.author.name}</div>
                    <div className="text-purple-400 text-sm mb-3">{post.author.role}</div>
                    <p className="text-text-secondary text-sm leading-relaxed">{post.author.bio}</p>
                  </div>
                </div>
              </div>
            </article>

            {/* Sidebar */}
            <aside className="hidden lg:block">
              <div className="sticky top-24 space-y-6">
                {/* Table of Contents */}
                <TableOfContents content={post.content} />

                {/* Related Posts */}
                {relatedPosts.length > 0 && (
                  <div className="card">
                    <h3 className="font-semibold mb-4 flex items-center justify-between">
                      <span>Benzer Yazılar</span>
                      <span className="text-xs text-text-tertiary font-normal">{relatedPosts.length} yazı</span>
                    </h3>
                    <div className="space-y-3 max-h-[500px] overflow-y-auto pr-1 custom-scrollbar">
                      {relatedPosts.map((relatedPost) => (
                        <Link
                          key={relatedPost.slug}
                          to={`/blog/${relatedPost.slug}`}
                          className="flex gap-3 group p-2 -mx-2 rounded-lg hover:bg-bg-elevated transition-colors"
                        >
                          {/* Thumbnail */}
                          <div className="w-16 h-16 rounded-lg overflow-hidden flex-shrink-0 bg-bg-elevated">
                            <img
                              src={relatedPost.image}
                              alt={relatedPost.title}
                              className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-300"
                            />
                          </div>
                          {/* Content */}
                          <div className="flex-1 min-w-0">
                            <h4 className="font-medium text-sm group-hover:text-purple-400 transition-colors line-clamp-2 leading-tight mb-1">
                              {relatedPost.title}
                            </h4>
                            <div className="flex items-center gap-2 text-text-tertiary text-xs">
                              <Clock size={12} />
                              <span>{relatedPost.readTime} okuma</span>
                            </div>
                          </div>
                        </Link>
                      ))}
                    </div>
                  </div>
                )}

                {/* Newsletter */}
                <div className="card bg-gradient-to-br from-purple-500/10 to-fuchsia-500/5 border-purple-500/20">
                  <h3 className="font-semibold mb-2">Bültene Abone Ol</h3>
                  <p className="text-text-secondary text-sm mb-4">
                    En yeni yazıları e-posta ile alın.
                  </p>
                  <input
                    type="email"
                    placeholder="E-posta adresiniz"
                    className="w-full px-3 py-2 rounded-lg bg-bg-elevated border border-border-default focus:border-purple-500 focus:outline-none text-sm mb-2"
                  />
                  <button className="btn btn-primary btn-sm w-full">
                    Abone Ol
                  </button>
                </div>
              </div>
            </aside>
          </div>
        </div>
      </section>

      {/* Back to Blog */}
      <section className="py-8 border-t border-border-default">
        <div className="container">
          <div className="flex items-center justify-between max-w-4xl mx-auto">
            <Link to="/blog" className="inline-flex items-center gap-2 text-text-secondary hover:text-purple-400 transition-colors">
              <ArrowLeft size={18} />
              Tüm Yazılar
            </Link>
            <Link to="/contact" className="btn btn-primary btn-sm">
              İletişime Geçin
              <ArrowRight size={16} />
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
